<template>
  <!-- Request Workflow History Modal -->
  <div v-if="show" class="modal show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-history me-2"></i>
            Workflow History - {{request.referenceNumber}}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading workflow history...</p>
          </div>

          <div v-else>
            <!-- Request Info Summary -->
            <div class="request-summary mb-4">
              <div class="card bg-light">
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Reference:</strong></p>
                      <p class="text-primary mb-0">{{request.referenceNumber}}</p>
                    </div>
                    <div class="col-md-3">
                      <p class="mb-1"><strong>LR Number:</strong></p>
                      <p class="text-info mb-0">{{request.LRNumber}}</p>
                    </div>
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Current Status:</strong></p>
                      <span class="badge" :class="getStatusBadge(request.status)">
                        {{request.status?.toUpperCase()}}
                      </span>
                    </div>
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Owner:</strong></p>
                      <p class="mb-0">{{request.ownerNames}}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Workflow Timeline -->
            <div v-if="workflowHistory.length > 0">
              <h6 class="mb-4">
                <i class="bx bx-git-branch me-2"></i>
                Workflow Timeline ({{workflowHistory.length}} events)
              </h6>

              <div class="timeline">
                <div
                    v-for="(event, index) in workflowHistory"
                    :key="event.id"
                    class="timeline-item"
                    :class="{'timeline-item-latest': index === 0}"
                >
                  <div class="timeline-marker" :class="getTimelineMarkerClass(event.new_status)">
                    <i :class="getStatusIcon(event.new_status)"></i>
                  </div>

                  <div class="timeline-content">
                    <div class="timeline-card">
                      <div class="timeline-header">
                        <div class="row align-items-center">
                          <div class="col-md-8">
                            <h6 class="mb-1">{{event.action_taken}}</h6>
                            <p class="text-muted mb-0">
                              <small>
                                <i class="bx bx-user me-1"></i>
                                {{event.performed_by}}
                                <span v-if="event.performed_by_id_no">({{event.performed_by_id_no}})</span>
                              </small>
                            </p>
                          </div>
                          <div class="col-md-4 text-md-end">
                            <span class="badge" :class="getStatusBadge(event.new_status)">
                              {{event.new_status?.toUpperCase()}}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="timeline-body">
                        <div class="row">
                          <div class="col-md-6">
                            <p class="mb-1">
                              <strong>Date & Time:</strong><br>
                              <i class="bx bx-calendar me-1"></i>
                              {{formatDateTime(event.performed_at)}}
                            </p>
                          </div>
                          <div class="col-md-6" v-if="event.previous_status">
                            <p class="mb-1">
                              <strong>Status Change:</strong><br>
                              <span class="badge bg-light text-dark me-1">{{event.previous_status}}</span>
                              <i class="bx bx-right-arrow-alt"></i>
                              <span class="badge" :class="getStatusBadge(event.new_status)">{{event.new_status}}</span>
                            </p>
                          </div>
                        </div>

                        <div v-if="event.comments" class="comments-section mt-3">
                          <p class="mb-1"><strong>Comments:</strong></p>
                          <div class="comments-box">
                            <p class="mb-0">{{event.comments}}</p>
                          </div>
                        </div>

                        <div v-if="event.ip_address || event.user_agent" class="technical-info mt-3">
                          <details>
                            <summary class="text-muted small" style="cursor: pointer;">
                              <i class="bx bx-info-circle me-1"></i>
                              Technical Information
                            </summary>
                            <div class="mt-2">
                              <p v-if="event.ip_address" class="mb-1">
                                <small><strong>IP Address:</strong> {{event.ip_address}}</small>
                              </p>
                              <p v-if="event.user_agent" class="mb-0">
                                <small><strong>User Agent:</strong> {{event.user_agent}}</small>
                              </p>
                            </div>
                          </details>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No History -->
            <div v-else class="no-history text-center py-5">
              <i class="bx bx-history text-muted" style="font-size: 4rem;"></i>
              <h6 class="text-muted mt-3">No Workflow History</h6>
              <p class="text-muted">No workflow events found for this request.</p>
            </div>

            <!-- Summary Statistics -->
            <div v-if="workflowHistory.length > 0" class="workflow-summary mt-4">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">
                    <i class="bx bx-chart-line me-2"></i>
                    Workflow Summary
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Total Events:</strong></p>
                      <p class="mb-0">{{workflowHistory.length}}</p>
                    </div>
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Processing Time:</strong></p>
                      <p class="mb-0">{{getProcessingTime()}}</p>
                    </div>
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Last Action:</strong></p>
                      <p class="mb-0">{{getLastAction()}}</p>
                    </div>
                    <div class="col-md-3">
                      <p class="mb-1"><strong>Handled By:</strong></p>
                      <p class="mb-0">{{getUniqueHandlers()}} person(s)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button v-if="workflowHistory.length > 0" type="button" class="btn btn-primary" @click="exportHistory">
            <i class="bx bx-export me-2"></i>Export History
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { execute2, landrate } from '@/api';

export default {
  name: 'RequestHistoryModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    request: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      workflowHistory: []
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.request.reference_number) {
        this.loadWorkflowHistory();
      }
    }
  },
  methods: {
    async loadWorkflowHistory() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getWorkflowHistory");
        data.append("referenceNumber", this.request.reference_number);

        const response = await execute2(data, landrate);

        if (response.data.success) {
          this.workflowHistory = response.data.data.workflow || [];
        } else {
          console.error('Failed to load workflow history:', response.data.message);
          this.workflowHistory = [];
        }
      } catch (error) {
        console.error('Error loading workflow history:', error);
        this.workflowHistory = [];
      } finally {
        this.loading = false;
      }
    },

    getStatusBadge(status) {
      const badges = {
        'pending': 'bg-warning text-dark',
        'under_review': 'bg-info text-white',
        'approved': 'bg-success text-white',
        'rejected': 'bg-danger text-white',
        'requires_documents': 'bg-secondary text-white'
      };
      return badges[status] || 'bg-light text-dark';
    },

    getTimelineMarkerClass(status) {
      const classes = {
        'pending': 'timeline-marker-warning',
        'under_review': 'timeline-marker-info',
        'approved': 'timeline-marker-success',
        'rejected': 'timeline-marker-danger',
        'requires_documents': 'timeline-marker-secondary'
      };
      return classes[status] || 'timeline-marker-default';
    },

    getStatusIcon(status) {
      const icons = {
        'pending': 'bx bx-time-five',
        'under_review': 'bx bx-search-alt',
        'approved': 'bx bx-check-circle',
        'rejected': 'bx bx-x-circle',
        'requires_documents': 'bx bx-file-blank'
      };
      return icons[status] || 'bx bx-circle';
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD/MM/YYYY HH:mm:ss');
    },

    getProcessingTime() {
      if (this.workflowHistory.length < 2) return 'N/A';

      const firstEvent = this.workflowHistory[this.workflowHistory.length - 1];
      const lastEvent = this.workflowHistory[0];

      const startTime = moment(firstEvent.performed_at);
      const endTime = moment(lastEvent.performed_at);

      const duration = moment.duration(endTime.diff(startTime));

      if (duration.days() > 0) {
        return `${duration.days()} days, ${duration.hours()} hours`;
      } else if (duration.hours() > 0) {
        return `${duration.hours()} hours, ${duration.minutes()} minutes`;
      } else {
        return `${duration.minutes()} minutes`;
      }
    },

    getLastAction() {
      if (this.workflowHistory.length === 0) return 'N/A';
      return this.workflowHistory[0].action_taken;
    },

    getUniqueHandlers() {
      const handlers = new Set(this.workflowHistory.map(event => event.performed_by));
      return handlers.size;
    },

    exportHistory() {
      // Create CSV export of workflow history
      const headers = ['Date & Time', 'Action', 'Previous Status', 'New Status', 'Performed By', 'Comments'];
      const rows = [headers.join(',')];

      this.workflowHistory.forEach(event => {
        const row = [
          `"${this.formatDateTime(event.performed_at)}"`,
          `"${event.action_taken}"`,
          `"${event.previous_status || 'N/A'}"`,
          `"${event.new_status}"`,
          `"${event.performed_by}"`,
          `"${event.comments || 'N/A'}"`
        ];
        rows.push(row.join(','));
      });

      const csvContent = rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `workflow_history_${this.request.reference_number}_${moment().format('YYYY-MM-DD')}.csv`;
      link.click();
    },

    closeModal() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block !important;
}

/* Timeline Styles */
.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dee2e6;
}

.timeline-item {
  position: relative;
  margin-bottom: 30px;
}

.timeline-item-latest .timeline-card {
  border-left: 4px solid #28a745;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.1);
}

.timeline-marker {
  position: absolute;
  left: -22px;
  top: 5px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  z-index: 2;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.timeline-marker-success {
  background: #28a745;
  color: white;
}

.timeline-marker-danger {
  background: #dc3545;
  color: white;
}

.timeline-marker-warning {
  background: #ffc107;
  color: #212529;
}

.timeline-marker-info {
  background: #17a2b8;
  color: white;
}

.timeline-marker-secondary {
  background: #6c757d;
  color: white;
}

.timeline-marker-default {
  background: #e9ecef;
  color: #6c757d;
}

.timeline-content {
  margin-left: 20px;
}

.timeline-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
}

.timeline-card:hover {
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.timeline-header h6 {
  color: #495057;
  font-weight: 600;
}

.timeline-body {
  margin-top: 15px;
}

.comments-box {
  background: #f8f9fa;
  border-left: 4px solid #007bff;
  padding: 12px;
  border-radius: 4px;
  font-style: italic;
}

.technical-info {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.card {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .modal-dialog {
    max-width: 95%;
    margin: 10px auto;
  }

  .timeline {
    padding-left: 20px;
  }

  .timeline-marker {
    left: -17px;
    width: 25px;
    height: 25px;
    font-size: 12px;
  }

  .timeline-content {
    margin-left: 15px;
  }

  .timeline-card {
    padding: 15px;
  }
}
</style>